import type { Query, QueryClient } from '@tanstack/react-query';
import { session } from '@/services/session';
import { deleteCrossAppCacheByPrefix } from '@/utils/cross-app-cache';
import { isExpiredSessionRejection, toAuthResolutionError } from './auth-resolution-error';
import { isTwoFactorSetupRejection } from './two-factor-setup';

/**
 * Cache-wide handler for a session-expiry 401 on *any* query — the Hydra
 * equivalent of the Ember app adapter's `handleUnauthorizedResponse`, which
 * routes every authenticated 401 into re-authentication rather than only the
 * auth bootstrap.
 *
 * `useAuthQuery` already opens the in-place re-auth modal when the auth query
 * itself is rejected; this extends the same trigger to the rest of the app so a
 * 401 on an ordinary query (not just the auth bootstrap or a reconnect refetch)
 * surfaces the modal too. It re-resolves the auth query — the single place that
 * classifies the rejection and routes it (modal for a dead session, `/login`
 * for a masq/no-user rejection) — the same path the Ember→shell delegation
 * takes via `announceRefreshAuth`.
 *
 * Guards, mirroring the Ember adapter:
 * - the auth query owns its own rejection, and re-resolving it from here would
 *   loop, so it's excluded;
 * - only while the shared session still reads authenticated — once a rejection
 *   is in flight the latch is already false and the modal is up, and this also
 *   skips 401s on pre-auth screens (Ember's `session.isAuthenticated` guard);
 * - only a 401 (expired session) opens re-auth; 403s and transient 5xx/network
 *   failures pass through untouched;
 * - a 401 for a user who must enroll in 2FA re-resolves the auth query too, but
 *   that resolution succeeds and reports the requirement, which sends the
 *   authenticated layout to the setup wizard instead of opening the modal.
 */
export function handleQueryAuthRejection(
  error: unknown,
  query: Query<unknown, unknown>,
  queryClient: QueryClient,
): void {
  if (query.queryKey[0] === 'authenticated') {
    return;
  }
  requestReauthentication(error, queryClient);
}

/**
 * Opens re-authentication for a request the server refused because the session expired: a 401 on
 * any request, while the session still reads authenticated. Mutations call it directly, because
 * the cache-wide handler only sees queries. Anything else passes through untouched.
 */
export function requestReauthentication(error: unknown, queryClient: QueryClient): void {
  if (!session.isAuthenticated.get()) {
    return;
  }
  if (!isExpiredSessionRejection(toAuthResolutionError(error, false))) {
    return;
  }
  // The cached account may predate an admin requiring 2FA, and re-reading it would then keep the
  // user on a page whose every request fails.
  if (isTwoFactorSetupRejection(error)) {
    deleteCrossAppCacheByPrefix('/accounts');
    deleteCrossAppCacheByPrefix('/account_by_environment');
  }
  void queryClient.invalidateQueries({ queryKey: ['authenticated'] });
}

/**
 * Cache-wide handler for a mutation the API refused because the user must enroll in 2FA. Only that
 * rejection: a mutation opts into session-expiry re-auth itself, by calling `requestReauthentication`.
 */
export function handleMutationTwoFactorRejection(error: unknown, queryClient: QueryClient): void {
  if (isTwoFactorSetupRejection(error)) {
    requestReauthentication(error, queryClient);
  }
}
